import React, { useEffect, useState } from 'react';
import { Button, Input, Card, CardContent, Alert, AlertDescription } from '@design-system';
import { Label } from '@client/components/ui/label';
import { getOauthProviderIcon, selectSession } from '@client';
import { useDispatch, useSelector } from 'react-redux';
import { selectPreLogin } from '@client';
import { fetchAuth0ConnectionTypeByEmailForPublicRequest, resetPreLoginStatus } from '@client';
import { isValidEmailFormat, SessionType, getBaseUrl } from '@shared';
import { useHomeNavigation } from '@client/hooks/useHomeNavigation';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuthorizationParams } from '@client/hooks/useAuthorizationParams';
import { MaintenanceNotice } from '@/components/MaintenanceNotice';
import { getDomainFromEmail } from '@shared';
import { ArrowLeft, Briefcase, ChevronRight, Loader2, Mail, User } from 'lucide-react';
import { WaterlilyLogo } from '@client/components/WaterlilyLogo';
import { useAuth0Redirect } from '@client/hooks/useAuth0Redirect';
import { Spinner } from '@design-system';

export interface LoginOptions {
  socialConnection: Extract<Auth0Connection, 'google-oauth2' | 'windowslive'> | undefined;
  email?: string;
}

const SHOW_MAINTENANCE_NOTICE = false;
const isLocalDev = () => getBaseUrl().includes('localhost');

export function Login() {
  const [searchParams] = useSearchParams();
  const emailHint = searchParams.get('email_hint');
  const connectionParam = searchParams.get('connection');
  const overrideConnection = searchParams.get('override_connection');

  const isPasswordMode = overrideConnection === 'password' && isLocalDev();
  const [email, setEmail] = useState<string>(emailHint ?? '');
  const [isValidEmail, setIsValidEmail] = useState(true);
  // Show signup options when user enters a new email (connection === 'none')
  const [showSignupOptions, setShowSignupOptions] = useState(false);

  const {
    status,
    error,
    connection,
    phoneNumber,
    email: preLoginEmail,
    isClientUser,
  } = useSelector(selectPreLogin);
  const { isAuthenticated, isLoading, loginWithRedirect, isRedirecting } = useAuth0Redirect();
  const {
    authorizationParams,
    searchParams: { clientSignIn, redirectTo },
  } = useAuthorizationParams();
  const dispatch = useDispatch();
  const session = useSelector(selectSession);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const isValidEmail = !!(email && email.length > 0) && isValidEmailFormat(email);
    setIsValidEmail(isValidEmail);
    if (!isValidEmail) {
      return;
    }

    if (isPasswordMode) {
      const safeRedirect = redirectTo && redirectTo !== '/login' ? redirectTo : '/';
      loginWithRedirect({
        authorizationParams: {
          ...authorizationParams,
          'connection': 'Username-Password-Authentication',
          'login_hint': email,
        },
        appState: {
          returnTo: safeRedirect,
        },
      });
      return;
    }

    // If showing signup options, proceed to create account with appropriate connection
    if (showSignupOptions) {
      const isInternalUser = getDomainFromEmail(email) === 'joinwaterlily.com';
      const shouldUseEmail = isInternalUser || overrideConnection === 'email';
      const signupConnection = shouldUseEmail ? 'email' : 'sms';

      loginWithRedirect({
        authorizationParams: {
          ...authorizationParams,
          'connection': signupConnection,
          'login_hint': signupConnection === 'sms' ? phoneNumber : email,
          'ext-email': signupConnection === 'sms' ? email : undefined,
        },
        appState: {
          returnTo: redirectTo || '/',
        },
      });
      return;
    }

    dispatch(fetchAuth0ConnectionTypeByEmailForPublicRequest(email));
  };

  const handleSocialLogin = (
    socialConnection: Extract<Auth0Connection, 'google-oauth2' | 'windowslive'>,
  ) => {
    loginWithRedirect({
      authorizationParams: {
        ...authorizationParams,
        connection: socialConnection,
      },
      appState: {
        returnTo: redirectTo || '/',
      },
    });
  };

  const handleBackClick = () => {
    dispatch(resetPreLoginStatus());
    setShowSignupOptions(false);
    setEmail('');
  };

  // Trigger Auth0 login
  useEffect(() => {
    if (isRedirecting || status === 'loading' || session.sessionType !== SessionType.unauthenticated) {
      return;
    }
    if (clientSignIn) {
      loginWithRedirect({
        authorizationParams: {
          ...authorizationParams,
          ...(connectionParam && { connection: connectionParam }),
        },
        appState: {
          returnTo: redirectTo || '/',
        },
      });
      return;
    }
    if (status === 'complete' && connection) {
      const finalEmail = preLoginEmail ?? email;

      // No advisor account found but email exists as a client user - redirect to client sign-in
      if (connection === 'none' && isClientUser) {
        loginWithRedirect({
          authorizationParams: {
            ...authorizationParams,
            'connection': 'email',
            'login_hint': finalEmail,
            'ext-client_sign_in': 'true',
          },
          appState: {
            returnTo: redirectTo || '/',
          },
        });
        return;
      }

      // No account found - show signup options
      if (connection === 'none') {
        setShowSignupOptions(true);
        return;
      }

      // Account exists - proceed to Auth0 with detected connection
      loginWithRedirect({
        authorizationParams: {
          ...authorizationParams,
          'connection': connection,
          'login_hint': connection === 'sms' ? phoneNumber : finalEmail,
          'ext-email': connection === 'sms' ? finalEmail : undefined,
        },
        appState: {
          returnTo: redirectTo || '/',
        },
      });
    }
  }, [
    status,
    connection,
    authorizationParams,
    emailHint,
    clientSignIn,
    isLoading,
    isAuthenticated,
    phoneNumber,
    email,
    isClientUser,
  ]);

  useHomeNavigation();

  const isFormLoading = status === 'loading';

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      {isRedirecting && (
        <div className="fixed inset-0 z-100 flex items-center justify-center bg-white/70">
          <Spinner className="size-8 text-primary" />
        </div>
      )}
      <header className="fixed left-0 right-0 top-0 z-50 flex items-center justify-between border-b bg-white px-4 py-4 md:px-6">
        <WaterlilyLogo className="h-6" />
        <Link
          to="/signup"
          className="text-sm text-gray-600 transition-colors duration-200 hover:text-gray-800"
        >
          Don't have an account?{' '}
          <span className="text-primary hidden font-medium sm:inline">Sign up</span>
          <span className="text-primary font-medium sm:hidden">Sign up</span>
        </Link>
      </header>

      <main className="flex flex-1 flex-col items-center justify-center px-4 pb-12 pt-18 md:pt-20">
        {SHOW_MAINTENANCE_NOTICE && (
          <div className="mb-6 w-full max-w-md">
            <MaintenanceNotice
              message="Some features may be temporarily unavailable due to scheduled maintenance. We're working to restore full functionality as quickly as possible."
              startTime=""
              endTime=""
            />
          </div>
        )}

        <div className="w-full max-w-md space-y-5">
          {/* Advisor Sign-In Card */}
          <Card className="overflow-hidden p-0 border bg-background gap-0 rounded-2xl shadow-none">
            {!showSignupOptions && (
              <div className='p-1'>
                <div className="bg-muted rounded-xl px-4 py-8 text-center">
                  <div className="mb-2 flex items-center justify-center gap-2">
                    <Briefcase className="size-5 text-primary-dark" />
                    <span className="text-xs font-semibold uppercase tracking-widest text-primary-dark">
                      Advisor Portal
                    </span>
                  </div>
                  <h1 className="text-xl font-bold text-gray-950">Sign-in to your advisor account</h1>
                </div>
              </div>
            )}
            {showSignupOptions && (
              <div className="p-1">
                <div className="rounded-xl bg-muted px-4 py-8 text-center">
                  <div className="mb-2 flex items-center justify-center gap-2">
                    <Briefcase className="size-5 text-primary-dark" />
                    <span className="text-xs font-semibold uppercase tracking-widest text-primary-dark">
                      Advisor Account
                    </span>
                  </div>
                  <h1 className="text-xl font-bold text-gray-950">Create your advisor account</h1>
                  <p className="mx-auto mt-1 max-w-xs text-sm text-foreground">
                    Choose how you'd like to get started
                  </p>
                </div>
              </div>
            )}
            <CardContent className="p-6">
              <form
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                {showSignupOptions && (
                  <>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full justify-center gap-2 rounded-lg border-gray-200 py-6 font-medium text-gray-700 transition-all duration-200 hover:bg-gray-50"
                        onClick={() => handleSocialLogin('google-oauth2')}
                      >
                        {getOauthProviderIcon('google')}
                        Google
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full justify-center gap-2 rounded-lg border-gray-200 py-6 font-medium text-gray-700 transition-all duration-200 hover:bg-gray-50"
                        onClick={() => handleSocialLogin('windowslive')}
                      >
                        {getOauthProviderIcon('microsoft')}
                        Outlook
                      </Button>
                    </div>

                    <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-gray-200">
                      <span className="relative z-10 bg-white px-4 text-gray-500">
                        Or continue with email
                      </span>
                    </div>
                  </>
                )}

                <div className="grid gap-2">
                  <Label
                    htmlFor="email"
                    className="flex items-center gap-2 text-sm font-medium text-gray-700"
                  >
                    <Mail className="h-4 w-4 text-gray-400" />
                    Email address
                  </Label>

                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    className="focus:border-primary focus:ring-primary h-12 rounded-lg border-gray-200 transition-all duration-200"
                    onChange={e => setEmail(e.target.value)}
                    disabled={showSignupOptions}
                    required
                  />
                  {!isValidEmail && (
                    <p className="text-sm text-red-700">Please enter a valid email address</p>
                  )}
                </div>

                {error && <ErrorMessage error={error} />}

                {showSignupOptions && (
                  <Alert
                    variant="warning"
                    className="border-amber-200 bg-amber-50"
                  >
                    <AlertDescription className="text-amber-800">
                      No account found. Create a new advisor account to get started.
                    </AlertDescription>
                  </Alert>
                )}

                <Button
                  type="submit"
                  className="py-6 w-full"
                  disabled={isFormLoading}
                >
                  {isFormLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {showSignupOptions ? 'Create Advisor Account' : 'Continue'}
                </Button>

                {showSignupOptions && (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    onClick={handleBackClick}
                    className="w-full text-foreground hover:text-gray-950"
                  >
                    <ArrowLeft className="size-3" />
                    Use different email
                  </Button>
                )}
              </form>
            </CardContent>
          </Card>

          {/* Consumer/Client Sign-In Option */}
          <Link
            to="/?client_sign_in=true"
            className="group block"
          >
            <Card className="border bg-primary/5 transition-all hover:border-primary/50 shadow-none border-primary/20 p-0 rounded-2xl">
              <CardContent className="flex p-4 md:p-6 items-center gap-4">
                <div className="flex size-12 shrink-0 items-center transition-all group-hover:bg-primary justify-center rounded-full bg-primary/20">
                  <User className="size-6 text-primary group-hover:text-primary-foreground transition-all" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-primary-dark group-hover:text-primary transition-all">
                    Looking for your intake form results?
                  </p>
                  <p className="text-sm text-primary-dark/80 group-hover:text-primary transition-all">
                    Click here to sign-in as a consumer for your results
                  </p>
                </div>
                <ChevronRight className="size-5 shrink-0 text-muted-foreground transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-accent-foreground group-hover:text-primary" />
              </CardContent>
            </Card>
          </Link>
        </div>
      </main>
    </div>
  );
}

function ErrorMessage({ error }: { error: string }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
      <svg
        className="h-4 w-4 shrink-0"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
          clipRule="evenodd"
        />
      </svg>
      <span className="font-medium">{error || 'Something went wrong. Please try again.'}</span>
    </div>
  );
}
